
import { 
  IStringNumOrStringHash 
} from "@models";
import { 
  Character,
  CharacterFeature,
  CharacterClass, 
  CharacterFeat, 
  CharacterItem,
  CharacterRace,
  CharacterASIBaseFeature,
  Ability,
  Attack,
  CreatureAbility,
  Spell,
  SpellsFromList,
  SkillAsAbility,
  CharacterSheetInterface,
  RollPlus,
  BasicCreatureInstance,
  SummonInstance,
  CharacterAction,
  SpellOverrides,
  Weapon,
  CharacterInfusion,
  CharacterInfusionForAlly,
  WEAPON_KEYWORDS,
  SpellsFromListWithCosts,
} from "@dndModels";
import { AbilityScoreType, AttackType, CastingTime, DamageType, EffectType, SpellLevel } from "@dndModels/Types";
import { ABILITY_SCORES } from "@dndModels/Constants";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from "@dndUtilities/data_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export class CharacterAbility {
  true_id = "";
  _name = "";
  sidekick_ability = false;
  sidekick_cast_type = "";
  hidden = false;
  show_upcasts: number[] = [];
  hide_special = false;
  hide_in_pdf = false;
  show_upcasts_in_pdf: number[] = [];
  hide_special_in_pdf = false;
  hide_ritual = false;
  char_feature: CharacterFeature | null = null;
  get creature_fb_type() {
    return this.char_feature?.creature_fb_type??"";
  }
  ability_type = ""; 
  source_id = "";
  source_type = ""; // Class, Race, Feat, or Item
  source_name = "";
  source_id2 = "";
  source_type2 = "";
  source_name2 = "";
  source_true_id = "";
  item_id = "";
  spellcasting_ability = "";
  spell_dc = 0;
  spell_attack = 0;
  attack: Attack = new Attack();
  attack_string = "";
  damage_string = "";
  // use_spellcasting_modifier: boolean = false;
  // special_resource_used: number = 0;
  special_resource_amount = -1;
  special_resource_refresh_rule = "";
  customizations: IStringNumOrStringHash = {};
  // favorite_options: string[] = [];
  base_slot_level: SpellLevel = -1; // If it uses spell slots, this is the lowest level
  calc_range = "";
  _spell: Spell | null = null;
  _spell_id = "";
  active = false;
  override_casting_time: CastingTime | "" = "";
  spell_overrides: SpellOverrides[] = [];
  favorite = false;
  infusion: CharacterInfusion | CharacterInfusionForAlly | undefined = undefined;

  get spell_id() {
    return this._spell_id === "" && this.the_spell ? this.the_spell._id : this._spell_id;
  }

  get ancestries() {
    return this.char_feature?.ancestries??[];
  }

  get level_string(): string {
    const level = this.level;
    let level_str = "";
    if (level === 0) {
      level_str = "Cantrip";
    } else if (level === 1) {
      level_str = "1st";
    } else if (level === 2) {
      level_str = "2nd";
    } else if (level === 3) {
      level_str = "3rd";
    } else {
      level_str = `${level}th`;
    }
    return level_str;
  }

  constructor(obj?: any) {
    if (obj) {
      this.true_id = obj.true_id ? obj.true_id : "";
      this._name = obj._name ? obj._name : "";
      this.hidden = obj.hidden ? obj.hidden : false;
      this.show_upcasts = obj.show_upcasts ? obj.show_upcasts : [];
      this.hide_special = obj.hide_special ? obj.hide_special : false;
      this.hide_in_pdf = obj.hide_in_pdf ? obj.hide_in_pdf : false;
      this.show_upcasts_in_pdf = obj.show_upcasts_in_pdf ? obj.show_upcasts_in_pdf : [];
      this.hide_special_in_pdf = obj.hide_special_in_pdf ? obj.hide_special_in_pdf : false;
      this.hide_ritual = obj.hide_ritual ? obj.hide_ritual : false;
      this.ability_type = obj.ability_type ? obj.ability_type : "";
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
      this.spell_attack = obj.spell_attack ? +obj.spell_attack : 0;
      this.attack = obj.attack ? obj.attack : new Attack();
      this.spell_dc = obj.spell_dc ? +obj.spell_dc : 0;
      this.customizations = obj.customizations ? obj.customizations : {};
      // this.favorite_options = obj.favorite_options ? obj.favorite_options : [];
      this.source_id = obj.source_id ? obj.source_id : "";
      this.source_type = obj.source_type ? obj.source_type : "";
      this.source_name = obj.source_name ? obj.source_name : "";
      this.source_id2 = obj.source_id2 ? obj.source_id2 : "";
      this.source_type2 = obj.source_type2 ? obj.source_type2 : "";
      this.source_name2 = obj.source_name2 ? obj.source_name2 : "";
      this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      this.item_id = obj.item_id ? obj.item_id : "";
      // this.special_resource_used = obj.special_resource_used ? obj.special_resource_used : 0;
      this.special_resource_amount = -1; // obj.special_resource_amount ? obj.special_resource_amount : -1;
      this.base_slot_level = obj.base_slot_level ? obj.base_slot_level : -1;
      this.attack_string = "";
      this.damage_string = "";
      this.active = obj.active ? obj.active : false;
      this.favorite = obj.favorite ? obj.favorite : false;
    }
    // this.recalc_attack_string(null);
  }

  get the_ability(): Ability | CreatureAbility | SkillAsAbility | SpellsFromList | SpellsFromListWithCosts | null { // ExtraSpellAsAbility | 
    const ff = this.char_feature && this.char_feature.floating_feature ? this.char_feature.floating_feature : null;
    if (ff instanceof Ability ||  
      ff instanceof SpellsFromList || 
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof CreatureAbility ||
      ff instanceof SkillAsAbility
    ) {
      return ff;
    }
    return null;
  }

  get name(): string {
    let name = this._name;
    const the_ability = this.the_ability;
    if (the_ability) {
      let ability_name = "";
      if ((the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) && this._spell) {
        if (this._spell) {
          ability_name = this._spell.name;
        }
      } else {
        ability_name = the_ability.name;
      }
      if (name === "") {
        name = ability_name;
      } else if (!name.includes(ability_name)) {
        name = ability_name + " - " + name;
      }
    }
    return name;
  }

  get description(): string {
    let description = "";
    const the_ability = this.the_ability;
    if (the_ability) {
      let ability_description = "";
      if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          ability_description = this._spell.description;
        }
      } else {
        ability_description = the_ability.description;
      }
      if (description === "") {
        description = ability_description;
      } else if (!description.includes(ability_description)) {
        description = ability_description + " - " + description;
      }
    }
    return description;
  }

  get true_name(): string {
    let name = this._name;
    const the_ability = this.the_ability;
    if (the_ability) {
      let ability_name = "";
      if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          ability_name = this._spell.true_name;
        }
      } else {
        ability_name = the_ability.true_name;
      }
      if (name === "") {
        name = ability_name;
      } else if (!name.includes(ability_name)) {
        name = ability_name + " - " + name;
      }
    }
    return name;
  }

  set name(value: string) {
    this._name = value;
  }

  get special_casting_time() {
    if (this.the_spell) {
      return this.the_spell.special_casting_time;
    } else {
      const the_ability = this.the_ability;
      if (the_ability instanceof Ability || the_ability instanceof CreatureAbility || the_ability instanceof SkillAsAbility) {
        return the_ability.special_casting_time;
      }
    }
    return "";
  }

  get duration() {
    if (this.the_spell) {
      return this.the_spell.duration;
    } else {
      const the_ability = this.the_ability;
      if (the_ability instanceof Ability || the_ability instanceof CreatureAbility) {
        return the_ability.duration;
      }
    }
    return "Instantaneous";
  }

  get level() {
    if (this.base_slot_level > -1) {
      return this.base_slot_level;
    } else {  
      const the_ability = this.the_ability;
      if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (the_ability.cast_at_level === "Don't Override") {
          if (this._spell) {
            return this._spell.level;
          }
        } else {
          return the_ability.cast_at_level;
        }
      } else if (the_ability instanceof Ability) {
        return the_ability.consumed_resource.slot_level;
      }
    }
    return 0;
  }

  get the_spell(): Spell | null {
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList || 
      the_ability instanceof SpellsFromListWithCosts) {
      return this._spell;
    } else if (this._spell) {
      return this._spell;
    } else {
      return null;
    }
  }

  set the_spell(value: Spell | null) {
    this._spell = value;
    if (value) {
      this._spell_id = value._id;
    } else {
      this._spell_id = "";
    }
  }

  get variant_of_id(): string {
    if (this.the_spell) {
      return this.the_spell.variant_of_id;
    }
    return "";
  }

  get variant_group_id(): string {
    if (this.the_spell) {
      return this.the_spell.variant_group_id;
    }
    return "";
  }

  get school(): string {
    const the_spell = this.the_spell;
    if (the_spell && the_spell.school) {
      return the_spell.school;
    }
    return "";
  }

  get effects() {
    const the_ability = this.the_ability;
    if (the_ability || this.sidekick_ability) {
      if (the_ability instanceof SkillAsAbility) {

      } else if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          return this._spell.effects;
        }
      } else if (the_ability) {
        return the_ability.effects;
      } else {
        if (this._spell) {
          return this._spell.effects;
        }
      }
    }
    return [];
  }

  get effect_types(): EffectType[] {
    return this.effects.map(o => o.type);
  }

  get attack_types(): AttackType[] {
    return this.effects.map(effect => { return effect.attack_type; });
  }

  get damage_types(): DamageType[] {
    return this.base_damage_types;
  }

  get base_damage_types(): DamageType[] {
    return this.effects.flatMap(effect => { return effect.damage_types; });
  }

  get main_damage_type() {
    const damage_type = this.base_damage_types[0];
    if (damage_type) {
      return damage_type;
    }
    return "Typeless";
  }

  get at_will(): boolean {
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList) {
      return the_ability.spell_overrides.slot_override === "At Will";
    } else if (the_ability instanceof SpellsFromListWithCosts) {
      return the_ability.spell_overrides.slot_override === "At Will";
    } 
    return false;
  }

  get ritual_only(): boolean {
    // I could make it work like this, but currently I don't want to support the idea of being able to 
    // modify a spell into a ritual only spell using a spell modifier feature.
    // return this.use_spell_overrides.some(o => o.ritual_override === "Ritual Only");
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList || 
      the_ability instanceof SpellsFromListWithCosts) {
      return the_ability.spell_overrides.ritual_override === "Ritual Only";
    }
    return false;
  }

  get ritual(): boolean {
    // I could make it work with use_spell_overrides, 
    // but I think this should only be supported from SpellsFromList features
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList || 
      the_ability instanceof SpellsFromListWithCosts) {
      if (the_ability.spell_overrides.ritual_override === "Ritual" || the_ability.spell_overrides.ritual_override === "Ritual Only") {
        return true;
      } else if (the_ability.spell_overrides.ritual_override === "No Ritual") {
        return false;
      }
      return this._spell?.ritual??false;
    }
    return false;
  }

  get use_spell_overrides() {
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList || 
      the_ability instanceof SpellsFromListWithCosts) {
      return [...this.spell_overrides,the_ability.spell_overrides];
    } else {
      return this.spell_overrides;
    }
  }

  get components(): string[] {
    if (this.the_spell) {
      let components = this.the_spell.components;
      const component_overrides = this.use_spell_overrides.filter(o => !o.components_override.includes("Don't Override"));
      for (const override of component_overrides) {
        if (override.components_override.length === 0) {
          components = [];
        } else {
          if (override.components_override.includes("No V")) {
            components = components.filter(o => o !== "V");
          }
          if (override.components_override.includes("No S")) {
            components = components.filter(o => o !== "S");
          }
          if (override.components_override.includes("No M")) {
            components = components.filter(o => o !== "M");
          }
        }
        if (components.length === 0) {
          break;
        }
      }
      return components;
    }
    return [];
  }

  get material_component(): string {
    if (this.the_spell) {
      return this.the_spell.material_component;
    } else {
      const the_ability = this.the_ability;
      if (the_ability instanceof Ability) {
        return the_ability.material_component;
      }
    }
    return "";
  }

  get material_consumed(): boolean {
    if (this.the_spell) {
      return this.the_spell.material_consumed;
    }
    return false;
  }

  get material_cost_listed(): boolean {
    if (this.the_spell) {
      return this.the_spell.material_cost_listed;
    }
    return false;
  }

  get components_string(): string {
    let components_string = "";
    const the_ability = this.the_ability;
    if (the_ability) {
      if (the_ability instanceof SkillAsAbility) {

      } else if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          if (this._spell.components) {
            if (this._spell.components.includes("V")) {
              components_string = "V";
            }
            if (this._spell.components.includes("S")) {
              if (components_string.length > 0) {
                components_string += ", ";
              }
              components_string += "S";
            }
            if (this._spell.components.includes("M")) {
              if (components_string.length > 0) {
                components_string += ", ";
              }
              components_string += "M";
              components_string += ` (${this._spell.material_component})`;
            }
          }
        }
      } else {
        if (the_ability.components) {
          if (the_ability.components.includes("V")) {
            components_string = "V";
          }
          if (the_ability.components.includes("S")) {
            if (components_string.length > 0) {
              components_string += ", ";
            }
            components_string += "S";
          }
          if (the_ability.components.includes("M")) {
            if (components_string.length > 0) {
              components_string += ", ";
            }
            components_string += "M";
            components_string += ` (${the_ability.material_component})`;
          }
        }
      }
    }
    return components_string;
  }

  get sight() {
    if (this.the_spell) {
      return this.the_spell.sight;
    } else {
      const the_ability = this.the_ability;
      if (the_ability instanceof Ability) {
        return the_ability.sight;
      }
    }
    return false;
  }

  get duration_string(): string {
    let duration_string = "";
    const the_ability = this.the_ability;
    if (the_ability) {
      if (the_ability instanceof SkillAsAbility) {

      } else if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          duration_string = this._spell.duration;
        }
      } else {
        duration_string = the_ability.duration;
      }
    }
    return duration_string;
  }

  use_string({ amount_consumed, obj, action, ignore_used = false }: {amount_consumed: number, obj?: CharacterSheetInterface | BasicCreatureInstance, action?: CharacterAction, ignore_used?: boolean}) {
    // if (this.name.includes("Bless")) {
    //   debugger;
    // }
    const the_ability = this.the_ability;
    if (the_ability instanceof Ability || 
      the_ability instanceof SpellsFromList || 
      the_ability instanceof SpellsFromListWithCosts) {
      let resource_consumed = the_ability instanceof Ability ? the_ability.consumed_resource.resource_consumed : the_ability.spell_overrides.consumed_resource.resource_consumed;
      // let amount_consumed = the_ability.amount_consumed;
      if (the_ability instanceof SpellsFromList) {
        if (the_ability.spell_overrides.slot_override === "Don't Override") {
          resource_consumed = "Slot";
          // amount_consumed = 1;
        } else if (the_ability.spell_overrides.slot_override === "At Will") {
          return "At Will";
        } else if (the_ability.spell_overrides.slot_override === "At Will or Slot Upcast") {
          return "At Will";
        }
      } else if (the_ability instanceof SpellsFromListWithCosts) {
        if (the_ability.spell_overrides.slot_override === "Don't Override") {
          resource_consumed = "Slot";
          // amount_consumed = 1;
        } else if (the_ability.spell_overrides.slot_override === "At Will") {
          return "At Will";
        } else if (the_ability.spell_overrides.slot_override === "At Will or Slot Upcast") {
          return "At Will";
        }
      }
      if (resource_consumed === "Special") {
        if (ignore_used) {
          return `${this.special_resource_amount} per ${this.special_resource_refresh_rule}`;  
        } else if (this.special_resource_refresh_rule.length > 0) {
          const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
          return `${special_resource_used}/${this.special_resource_amount} per ${this.special_resource_refresh_rule}`;
        } else {
          const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
          return `${special_resource_used}/${this.special_resource_amount}`;
        }
      } else if (resource_consumed === "Recharge") {
        if (the_ability instanceof Ability) {
          const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
          if (special_resource_used === 1) {
            return "Ready";
          } else {
            return `Recharges on ${ the_ability.consumed_resource.recharge_on.join(" or ")} (d${the_ability.consumed_resource.recharge_die})`;
          }
        }
      } else if (resource_consumed === "None") {
        return "At Will";
      } else if (obj) {
        const resource_finder = obj instanceof BasicCreatureInstance ? [] : obj.resources.filter(o => 
          o.type_id === resource_consumed);
        if (resource_finder.length === 1) {
          const resource = resource_finder[0];
          if (ignore_used) {
            return `${amount_consumed} ${amount_consumed > 1 ? resource.name : resource.singular} of ${resource.total}`;
          } else {
            return `${amount_consumed} ${amount_consumed > 1 ? resource.name : resource.singular}|${resource.used}/${resource.total}`;
          }
        }
      }
    } else if (the_ability instanceof CreatureAbility) {
      if (the_ability.consumed_resource.resource_consumed === "Recharge") {
        const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
        if (special_resource_used === 0) {
          return "Ready";
        } else {
          return `Recharges on ${ the_ability.consumed_resource.recharge_on.join(" or ")} (d${the_ability.consumed_resource.recharge_die})`;
        }
      } 
    } else if (this.sidekick_ability && action) {
      const resource_consumed = action.special_resource ? "Special" : "Slot";
      if (resource_consumed === "Special") {
        if (ignore_used) {
          return `${this.special_resource_amount} per ${this.special_resource_refresh_rule}`;  
        } else if (this.special_resource_refresh_rule.length > 0) {
          const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
          return `${special_resource_used}/${this.special_resource_amount} per ${this.special_resource_refresh_rule}`;
        } else {
          const special_resource_used = obj?.resource_holder.getSpecialResourceUsed(this.true_id)??0;
          return `${special_resource_used}/${this.special_resource_amount}`;
        }
      } else if (obj) {
        const resource_finder = obj instanceof BasicCreatureInstance ? [] : obj.resources.filter(o => 
          o.type_id === resource_consumed);
        if (resource_finder.length === 1) {
          const resource = resource_finder[0];
          if (ignore_used) {
            return `${amount_consumed} ${amount_consumed > 1 ? resource.name : resource.singular} of ${resource.total}`;
          } else {
            return `${amount_consumed} ${amount_consumed > 1 ? resource.name : resource.singular}|${resource.used}/${resource.total}`;
          }
        }
      }
    }
    return "Use";
  }

  get use_spellcasting_modifier() {
    const use_mod_overrides = this.use_spell_overrides.find(o => o.include_modifier_on_damage === "True");
    return use_mod_overrides !== undefined;
  }

  get damage_die_size_override() {
    const damage_die_size_override = this.use_spell_overrides.find(o => o.damage_die_size_override !== -1);
    return damage_die_size_override?.damage_die_size_override??-1;
  }

  get casting_times_old(): CastingTime[] {
    if (this.override_casting_time === "") {
      const the_spell = this.the_ability;
      if (the_spell instanceof SpellsFromList || 
        the_spell instanceof SpellsFromListWithCosts) {
        if (this._spell) {
          return [this._spell.casting_time];
        }
      } else if (the_spell instanceof Ability) {
        return [the_spell.casting_time];
      }
      return ["A"];
    } else {
      return [this.override_casting_time];
    }
  }

  get casting_times(): CastingTime[] {
    // if (this.name.includes("Find Familiar")) {
    //   debugger;
    // }
    const casting_times_old = this.casting_times_old;
    const casting_time_overrides = this.use_spell_overrides.filter(o => o.casting_time_override !== "Don't Override");
    if (casting_time_overrides.length === 0) {
      return casting_times_old;
    } else {
      const options: CastingTime[] = [];
      for (const override of casting_time_overrides) {
        if (override.casting_time_override === "Options") {
          for (const opt of override.casting_time_options) {
            if (opt === "Don't Override") {
              for (const old of casting_times_old) {
                if (!options.includes(old)) {
                  options.push(old);
                }
              }
            } else if (!options.includes(opt)) {
              options.push(opt);
            }
          }
        } else if (override.casting_time_override === "Don't Override") {
          // Will never come here.  It's just to make the TS happy for the next one.
        } else if (!options.includes(override.casting_time_override)) {
          options.push(override.casting_time_override);
        }
      }
      return options;
    }
  }

  get concentration(): boolean {
    const the_ability = this.the_ability;
    if (the_ability) {
      if (the_ability instanceof SpellsFromList || 
        the_ability instanceof SpellsFromListWithCosts) {
        if (the_ability.spell_overrides.concentration_override === "Don't Override") {
          if (this._spell) {
            return this._spell.concentration;
          }
        } else {
          return the_ability.spell_overrides.concentration_override === "Concentration";
        }
      } else if (!(the_ability instanceof SkillAsAbility)) {
        return the_ability.concentration;
      }
    }
    if (this._name !== "") {
      // This means it's not the real one, it's just here as part of concentrating_on
      return true; 
    }
    return false;
  }

  get can_be_active(): boolean {
    return !this.concentration && this.duration_string !== "Instantaneous";
  }

  get the_source_name(): string {
    // if (["Potion Of Healing","Potion of Healing"].includes(this.source_name)) {
    if (this.char_feature) {
      return this.char_feature.source_name;
    } else {
      return this.source_name;
    }
    // } else {
    //   return this.source_name;
    // }
  }

  get knowledge_usage_key(): string {
    if (this._spell) {
      return this._spell.knowledge_usage_key;
    }
    return "";
  }

  get knowledge_type() {
    if (this._spell) {
      return this._spell.knowledge_type;
    }
    return "None";
  }

  get obj_filter() {
    if (this._spell) {
      return this._spell.obj_filter;
    }
    return {};
  }

  get counts_as_weapon() {
    const the_ability = this.the_ability;
    if (the_ability instanceof Ability) {
      return the_ability.counts_as_weapon;
    }
    return "";
  }

  getResourceStuff(action: CharacterAction, slot_type: string) {
    let resource_consumed = action.resource_consumed;
    let amount_consumed = action.amount_consumed;
    let and_special = action.and_special;
    let slot = action.slot;
    const type = action.type;
    const the_ability = this.the_ability;
    if (the_ability instanceof SpellsFromList) {
      if (the_ability.spell_overrides.slot_override === "Don't Override") {
        resource_consumed = "Slot";
        amount_consumed = 1;
        slot = true;
      } else if (the_ability.spell_overrides.slot_override === "Or Special Resource") {
        if (type === "Spell") {
          resource_consumed = "Slot";
          amount_consumed = 1;
          slot = true;
        } else {
          if (slot_type === "Special" || (!slot && slot_type === "Slots")) {
            resource_consumed = the_ability.resource_consumed;
            amount_consumed = the_ability.spell_overrides.consumed_resource.amount_consumed;
          } else if (slot_type === "Charge") {
            console.log("Not implemented");
          } else {
            resource_consumed = slot_type;
            amount_consumed = the_ability.spell_overrides.consumed_resource.amount_consumed;
          }
        }
      } else if (the_ability.spell_overrides.slot_override === "And Special Resource") {
        resource_consumed = slot_type;
        amount_consumed = the_ability.spell_overrides.consumed_resource.amount_consumed;
        and_special = true;
        slot = true;
      } else if (the_ability.spell_overrides.slot_override === "Only Special Resource") {
        resource_consumed = the_ability.resource_consumed;
        amount_consumed = the_ability.spell_overrides.consumed_resource.amount_consumed;
      }
    } else if (the_ability instanceof SpellsFromListWithCosts && this._spell) {
      const s = common_util.getSpellWithOverridableCostFromSFLWC(this._spell, the_ability);
      if (s) {
        if (s.cost_override.slot_override === "Don't Override") {
          resource_consumed = "Slot";
          amount_consumed = 1;
          slot = true;
        } else if (s.cost_override.slot_override === "Or Special Resource") {
          if (type === "Spell") {
            resource_consumed = "Slot";
            amount_consumed = 1;
            slot = true;
          } else {
            if (slot_type === "Special") {
              resource_consumed = s.cost_override.consumed_resource.resource_consumed;
              amount_consumed = s.cost_override.consumed_resource.amount_consumed;
            } else if (slot_type === "Charge") {
              console.log("Not implemented");
            } else {
              resource_consumed = slot_type;
              amount_consumed = s.cost_override.consumed_resource.amount_consumed;
            }
          }
        } else if (s.cost_override.slot_override === "And Special Resource") {
          resource_consumed = slot_type;
          amount_consumed = s.cost_override.consumed_resource.amount_consumed;
          and_special = true;
          slot = true;
        } else if (s.cost_override.slot_override === "Only Special Resource") {
          resource_consumed = s.cost_override.consumed_resource.resource_consumed;
          amount_consumed = s.cost_override.consumed_resource.amount_consumed;
        }
      }
    } else if (the_ability instanceof Ability) {
      resource_consumed = the_ability.consumed_resource.resource_consumed;
      amount_consumed = the_ability.consumed_resource.amount_consumed;
    } else if (this.sidekick_ability) {
      if (this.at_will) {
        resource_consumed = "At Will";
        // disabled = false;
      } else {
        amount_consumed = 1;
        if (action.special_resource) {
          resource_consumed = "Special";
          // const used = character.resource_holder.getSpecialResourceUsed(obj.true_id);
          // disabled = used + amount_consumed >= obj.special_resource_amount;
        } else {
          resource_consumed = "Slot";
          // if (char_slot) {
          //   disabled = char_slot.used === char_slot.total;
          // }
        }
      }
    }
    return { resource_consumed, amount_consumed, and_special, slot };
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      _name: this._name,
      hidden: this.hidden,
      show_upcasts: this.show_upcasts,
      hide_special: this.hide_special,
      hide_in_pdf: this.hide_in_pdf,
      show_upcasts_in_pdf: this.show_upcasts_in_pdf,
      hide_special_in_pdf: this.hide_special_in_pdf,
      hide_ritual: this.hide_ritual,
      ability_type: this.ability_type,
      customizations: this.customizations,
      // favorite_options: this.favorite_options,
      source_type: this.source_type,
      source_id: this.source_id,
      source_name: this.source_name,
      source_true_id: this.source_true_id,
      item_id: this.item_id,
      // special_resource_used: this.special_resource_used,
      base_slot_level: this.base_slot_level,
      spell_id: this._spell_id,
      active: this.active,
      favorite: this.favorite
    };
  }

  copyFeature = (cf: CharacterFeature, 
    char_int: CharacterSheetInterface | BasicCreatureInstance | SummonInstance, 
    char: Character | undefined,
    true_id = "") => {
    // if (cf.name.includes("Telekinetic")) {
    //   debugger;
    // }
    this.source_type = cf.root_type;
    this.source_id = cf.root_id;
    this.source_name = cf.root_name;
    this.source_id2 = cf.use_source_id;
    this.source_true_id = cf.source_true_id;
    this.item_id = cf.item_id;
    if (cf.floating_feature && 
      (cf.floating_feature instanceof Ability ||
      cf.floating_feature instanceof SpellsFromList || 
      cf.floating_feature instanceof SpellsFromListWithCosts ||
      cf.floating_feature instanceof SkillAsAbility ||
      cf.floating_feature instanceof CreatureAbility)) {
      this.ability_type = cf.floating_feature.feature_type;
      this.char_feature = cf;
      // if (this.the_ability.description.length === 0) {
      //   this.the_ability.description = feature.floating_feature.description;
      // }
      if (char && (cf.floating_feature instanceof SpellsFromList || 
        cf.floating_feature instanceof SpellsFromListWithCosts)) { 
        const spell_as_ability = cf.floating_feature;
        const spell_overrides = spell_as_ability.spell_overrides;
        if (spell_overrides.count_as_class_id !== "" && spell_overrides.count_as_class_id !== "None" && spell_overrides.count_as_class_id !== "Don't Override") {
          const class_finder = char.classes.find(o => o.source_id === spell_overrides.count_as_class_id);
          if (class_finder) {
            this.spellcasting_ability = class_finder.spellcasting_ability;
            this.spell_attack = class_finder.spell_attack;
            this.spell_dc = class_finder.spell_dc;
          }
        } else if (spell_overrides.spellcasting_ability !== "Don't Override") {
          if (spell_overrides.spellcasting_ability === "Spellcasting" && char) {
            const class_finder = char.classes.filter(o => o.true_id === cf.source_true_id || o.source_id === cf.root_id || o.subclass_id === cf.root_id);
            if (class_finder.length === 1) {
              this.spellcasting_ability = class_finder[0].spellcasting_ability;
              this.spell_attack = class_finder[0].spell_attack;
              this.spell_dc = class_finder[0].spell_dc;
            } else {
              // It may be from something other than a class, but wants to use a class's spellcasting.
              // Get the highest one
              let max_dc = 0;
              let max_attack = 0;
              let max_ability = "";
              for (const char_class of char.classes) {
                if (char_class.spellcasting_ability !== "None") {
                  if (char_class.spell_dc > max_dc) {
                    max_ability = char_class.spellcasting_ability;
                    max_dc = char_class.spell_dc;
                    max_attack = char_class.spell_attack;
                  }
                }
              }
              this.spellcasting_ability = max_ability as AbilityScoreType;
              this.spell_attack = max_attack;
              this.spell_dc = max_dc;
            }
          } else if (spell_overrides.spellcasting_ability === "Same as Feat" && !(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance)) {
            const asi_finder = char_int.asi_features.filter(o => o.use_source_id === cf.use_source_id);
            if (asi_finder.length === 1) {
              const char_asi_base_feature = asi_finder[0].feature_options[0] as CharacterASIBaseFeature;
              if (char_asi_base_feature && 
                char_asi_base_feature.asi_features.length > 0 && 
                ABILITY_SCORES.includes(char_asi_base_feature.asi_features[0].selected_option)) {
                this.spellcasting_ability = char_asi_base_feature.asi_features[0].selected_option as AbilityScoreType;
              }
            }
          } else if (spell_overrides.spellcasting_ability === "Selectable" && char) {
            const source = char.all_char_feature_sources.find(o => o.true_id === cf.source_true_id);
            if (source && source.selectable_hash[spell_overrides.spellcasting_ability_selectable_key]) {
              this.spellcasting_ability = source.selectable_hash[spell_overrides.spellcasting_ability_selectable_key][0] as AbilityScoreType;
            }
          } else {
            this.spellcasting_ability = spell_overrides.spellcasting_ability as AbilityScoreType;
          }
        }
        if (spell_overrides.override_dc_atk_mod) {
          this.spell_attack = char_util.value(spell_overrides.spell_attack, { obj: char });
          this.spell_dc = char_util.value(spell_overrides.spell_dc, { obj: char });
          // TODO: Make the mod set on here as well.
        }
        // } else if (char && (cf.floating_feature instanceof Ability || 
        //   cf.floating_feature instanceof CreatureAbility)) {
        //   this.spell_attack = char_util.value(cf.floating_feature.attack_bonus, { obj: char, root_id: cf.source_true_id, item_id: cf.item_id });
        //   this.spell_dc = char_util.value(cf.floating_feature.dc, { obj: char, source_id: cf.source_id, root_id: cf.source_true_id, item_id: cf.item_id });
      } else if (char && cf.floating_feature instanceof Ability && cf.floating_feature.counts_as_weapon !== "") {
        const counts_as_weapon = cf.floating_feature.counts_as_weapon;
        const weapon = data_util.items.find(o => o._id === counts_as_weapon);
        if (weapon instanceof Weapon) {
          const weapon_keywords = WEAPON_KEYWORDS.filter(o => weapon.weapon_keyword_ids.includes(o._id));
          const attack_stats: AbilityScoreType[] = [];
          for (const wk of weapon_keywords) {
            if ( wk.use_ability_score !== "Default" && !attack_stats.includes(wk.use_ability_score)) {
              attack_stats.push(wk.use_ability_score);
            }
            if (wk.alternate_ability_score !== "None" && !attack_stats.includes(wk.alternate_ability_score)) {
              attack_stats.push(wk.alternate_ability_score);
            }
          }
          if (attack_stats.length > 1) {
            // Find the best one.
            let max = 0;
            let max_ability: AbilityScoreType | "None" = "None";
            for (const stat of attack_stats) {
              const char_stat = char.current_ability_scores.getAbilityScore(stat);
              if (char_stat > max) {
                max = char_stat;
                max_ability = stat;
              }
            }
            if (max_ability !== "None") {
              this.spellcasting_ability = max_ability;
            }
          }
        }
      }
      this.true_id = true_id === "" ? cf.true_id : true_id;
      this.setAttackAndDC(char_int);
    }
  }

  connectFeature = (feature: CharacterFeature, char_int: CharacterSheetInterface,
    char: Character, true_id = "") => {
    this.copyFeature(feature, char_int, char, true_id);
  }

  setAttackAndDC = (char: CharacterSheetInterface | BasicCreatureInstance | SummonInstance) => {
    // if (this.name.includes("Fire Finger Gun")) {
    //   debugger;
    // }
    const the_ability = this.the_ability;
    if (the_ability instanceof Ability) {
      const source_id = this.char_feature ? this.char_feature.source_true_id : "";
      const root_id = this.char_feature ? this.char_feature.source_true_id : "";
      this.spell_attack = char_util.value(the_ability.attack_bonus, { obj: char, source_id, root_id });
      this.spell_dc = char_util.value(the_ability.dc, { obj: char, source_id, root_id });
    } else if (the_ability instanceof CreatureAbility) {
      const source_id = this.char_feature ? this.char_feature.source_true_id : "";
      const root_id = this.char_feature ? this.char_feature.source_true_id : "";
      this.spell_attack = char_util.value(the_ability.attack_bonus, { obj: char, source_id, root_id }); // , log_it: this.name.includes("Bite")
      this.spell_dc = char_util.value(the_ability.dc, { obj: char, source_id, root_id });
      this.attack = new Attack();
      const attack_roll = new RollPlus();
      attack_roll.flat = this.spell_attack;
      this.attack.attack_rolls.push(attack_roll);
    }
  }

  connectSource(source: CharacterClass | CharacterRace | CharacterFeat | CharacterItem) {
    if (source instanceof CharacterClass) {
      this.source_type = "Class";
      this.source_id = source.source_id;
      this.spellcasting_ability = source.spellcasting_ability;
      this.spell_attack = +source.spell_attack;
      this.spell_dc = +source.spell_dc;
      if (source.source) {
        this.source_name = source.source.name;
      }
    } else if (source instanceof CharacterFeat) {
      this.source_type = "Feat";
      this.source_id = source.source_id;
      if (source.source) {
        this.source_name = source.source.name;
      }
    } else if (source instanceof CharacterItem) {
      this.source_type = "Item";
      this.source_id = source.true_id;
      this.source_name = source.name;
    } else if (source instanceof CharacterRace) {
      this.source_type = "Race";
      this.source_id = source.source_id;
      if (source.source) {
        this.source_name = source.source.name;
      }
    }
  }

  calc_special_resource_amount(char: CharacterSheetInterface) {
    // if (this.name.includes("Misty Step")) {
    //   debugger;
    // }
    const the_ability = this.the_ability;
    if (the_ability) {
      if (the_ability instanceof Ability && 
        the_ability.consumed_resource.resource_consumed === "Special") {
        this.special_resource_amount = char_util.value(the_ability.consumed_resource.special_resource_amount, { obj: char, source_id: this.source_true_id });
        this.special_resource_refresh_rule = the_ability.consumed_resource.special_resource_refresh_rule;
      } else if ((the_ability instanceof SpellsFromList) && 
        the_ability.spell_overrides.consumed_resource.resource_consumed === "Special") {
        this.special_resource_amount = char_util.value(the_ability.spell_overrides.consumed_resource.special_resource_amount, { obj: char, source_id: this.source_true_id });
        this.special_resource_refresh_rule = the_ability.spell_overrides.consumed_resource.special_resource_refresh_rule;
      } else if (the_ability instanceof SpellsFromListWithCosts && this._spell &&
        the_ability.spell_overrides.consumed_resource.resource_consumed === "Special") {
        this.special_resource_amount = char_util.value(the_ability.spell_overrides.consumed_resource.special_resource_amount, { obj: char, source_id: this.source_true_id });
        this.special_resource_refresh_rule = the_ability.spell_overrides.consumed_resource.special_resource_refresh_rule;
      }
    }
  }

  clone(drop_ids = false): CharacterAbility {
    const new_obj = new CharacterAbility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: CharacterAbility, drop_ids = false) {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this._name = copyMe._name;
    this.hidden = copyMe.hidden;
    this.show_upcasts = [...copyMe.show_upcasts];
    this.hide_special = copyMe.hide_special;
    this.hide_in_pdf = copyMe.hide_in_pdf;
    this.show_upcasts_in_pdf = [...copyMe.show_upcasts_in_pdf];
    this.hide_special_in_pdf = copyMe.hide_special_in_pdf;
    this.hide_ritual = copyMe.hide_ritual;
    this.ability_type = copyMe.ability_type;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spell_attack = copyMe.spell_attack
    this.attack = copyMe.attack;
    this.spell_dc = copyMe.spell_dc;
    this.customizations = {...copyMe.customizations};
    // this.favorite_options = [...copyMe.favorite_options];
    this.source_id = copyMe.source_id;
    this.source_type = copyMe.source_type;
    this.source_name = copyMe.source_name;
    this.source_id2 = copyMe.source_id2;
    this.source_type2 = copyMe.source_type2;
    this.source_name2 = copyMe.source_name2;
    // this.special_resource_used = copyMe.special_resource_used;
    this.special_resource_amount = copyMe.special_resource_amount;
    this.base_slot_level = copyMe.base_slot_level;
    this.attack_string = copyMe.attack_string;
    this.damage_string = copyMe.damage_string;
    this.char_feature = copyMe.char_feature;
    this._spell_id = copyMe._spell_id;
    this._spell = copyMe._spell;
    this.source_true_id = copyMe.source_true_id;
    this.item_id = copyMe.item_id;
    this.active = copyMe.active;
    this.favorite = copyMe.favorite;
  }
}